'use client'

import { Play } from 'lucide-react'
import { useState } from 'react'

interface TestimonialSectionProps {
  videoUrl?: string
}

export const TestimonialSection = ({ videoUrl }: TestimonialSectionProps) => {
  const [isPlaying, setIsPlaying] = useState(false)

  const handlePlay = () => {
    setIsPlaying(true)
  }

  const getYouTubeVideoId = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    return match && match[2].length === 11 ? match[2] : null
  }

  const videoId = videoUrl ? getYouTubeVideoId(videoUrl) : null

  return (
    <section className="mb-20">
      <div className="relative overflow-hidden rounded-3xl bg-linear-to-br from-yellow-900 via-yellow-600 to-yellow-900 p-4 lg:p-12">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 h-32 w-32 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-10 left-10 h-40 w-40 rounded-full bg-yellow-400 blur-3xl" />
          <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 h-24 w-24 transform rounded-full bg-red-400 blur-2xl" />
        </div>

        <div className="relative z-10">
          <div className="mb-12 text-center">
            <div className="mb-4 inline-flex items-center gap-3">
              <div className="h-8 w-1 rounded-full bg-linear-to-b from-white to-orange-200" />
              <h2 className="font-bold text-3xl text-white lg:text-4xl">
                #Eduhot: Depoimentos
              </h2>
              <div className="h-8 w-1 rounded-full bg-linear-to-b from-white to-orange-200" />
            </div>
            <p className="mx-auto max-w-2xl text-lg text-orange-100 leading-relaxed">
              Veja o que nossos alunos têm a dizer sobre a experiência EDUHOT
            </p>
          </div>

          <div className="mx-auto max-w-4xl">
            <div className="rounded-2xl border border-white/20 bg-white/95 p-8 shadow-2xl backdrop-blur-xs lg:p-10">
              <div className="flex flex-col items-start gap-6 lg:flex-row">
                <div className="flex items-center gap-4 lg:flex-col lg:items-center lg:text-center">
                  <div className="relative">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-linear-to-br from-purple-500 to-purple-600 shadow-lg lg:h-20 lg:w-20">
                      <span className="font-bold text-lg text-white lg:text-xl">
                        A
                      </span>
                    </div>
                    <div className="-bottom-1 -right-1 absolute flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-green-500">
                      <div className="h-2 w-2 rounded-full bg-white" />
                    </div>
                  </div>
                  <div className="lg:text-center">
                    <h3 className="font-bold text-gray-900 text-lg">
                      Ana Paula S.
                    </h3>
                    <p className="text-gray-500 text-sm">Aluna EDUHOT</p>
                    <div className="mt-1 flex items-center gap-1 lg:justify-center">
                      {[...Array(5)].map((_, i) => (
                        <div
                          className="h-3 w-3 rounded-full bg-yellow-400"
                          key={i}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex-1 space-y-4">
                  <div className="relative">
                    <svg
                      className="-top-2 -left-2 absolute h-8 w-8 text-orange-200"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v7h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v7h-9.983z" />
                    </svg>
                    <blockquote className="pl-8 text-gray-700 text-lg leading-relaxed">
                      "Eu amei a plataforma. Conteúdos ricos de informação.
                      Nível de prova acessível para quem busca horas
                      complementares ou para prova de títulos. Eu super
                      recomendo."
                    </blockquote>
                  </div>

                  {videoId && (
                    <div className="relative mx-auto w-full max-w-md">
                      <div className="relative aspect-video overflow-hidden rounded-2xl bg-gray-900">
                        {isPlaying ? (
                          <iframe
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="h-full w-full"
                            frameBorder="0"
                            height="100%"
                            referrerPolicy="strict-origin-when-cross-origin"
                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                            title="YouTube video player"
                            width="100%"
                          />
                        ) : (
                          <>
                            <div className="flex h-full w-full items-center justify-center bg-gray-900">
                              <img
                                alt="Video thumbnail"
                                className="h-full w-full object-cover"
                                onError={e => {
                                  const target = e.target as HTMLImageElement
                                  target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                                }}
                                src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                              />
                            </div>

                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-xs">
                              <button
                                aria-label="Reproduzir vídeo"
                                className="group relative flex h-20 w-20 items-center justify-center rounded-2xl bg-linear-to-br from-red-500 to-red-600 shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-3xl focus:outline-hidden focus:ring-4 focus:ring-red-300 lg:h-24 lg:w-24"
                                onClick={handlePlay}
                                type="button"
                              >
                                <Play className="ml-1 h-8 w-8 text-white lg:h-10 lg:w-10" />
                                <div className="absolute inset-0 rounded-2xl bg-linear-to-br from-red-500 to-red-600 opacity-30 blur-xl transition-opacity duration-300 group-hover:opacity-50" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 border-gray-100 border-t pt-6">
                <div className="flex flex-wrap items-center justify-center gap-4 text-gray-500 text-sm lg:justify-start">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <span>Depoimento verificado</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                    <span>Aluna ativa</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-orange-500" />
                    <span>5+ cursos concluídos</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
